import React, { useState, useEffect } from 'react';
import { appPath } from '../../../utils/appPaths.js';
import { Spinner, Pagination, PaginationItem, PaginationLink, Input } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Edit, AlertCircle, ChevronLeft, ChevronRight, Filter, ExternalLink } from 'react-feather';
import './unknown-artist-distributions.css';
import ProcessingReportService from '../../../Services/reports/ProcessingReportService';
import IncomeService from '../../../Services/income/IncomeService';

const DISTRIBUTION_TABS = [
    { id: 'title', label: 'Title' },
    { id: 'dsp', label: 'DSP' },
    { id: 'country', label: 'Country' },
    { id: 'typesale', label: 'Sale type' },
];

const COLUMN_HEADERS = {
    title: 'Title',
    dsp: 'DSP',
    country: 'Country',
    typesale: 'Sale type',
};

const UnknownArtistDistributions = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('title');
    const [distributionData, setDistributionData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [incomeReports, setIncomeReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [isFilterActive, setIsFilterActive] = useState(false);

    // Distribution counts - will be updated based on selected report
    const [distributionCounts, setDistributionCounts] = useState({
        title: 0,
        dsp: 0,
        country: 0,
        typesale: 0,
    });

    // State to store the latest report data
    const [latestReportId, setLatestReportId] = useState(null);

    // Fetch income reports and set the latest one
    const fetchIncomeReports = async () => {
        setIsLoadingReports(true);
        try {
            const response = await IncomeService.getAllIncomes();
            if (response.success && response.data && response.data.length > 0) {
                const reports = response.data || [];
                setIncomeReports(reports);

                // Find the latest report by ID (highest ID)
                if (reports.length > 0) {
                    // Sort reports by ID (descending) to get the latest
                    const sortedReports = [...reports].sort((a, b) => b.id - a.id);
                    const latest = sortedReports[0];
                    const latestIncomeId = latest.id;
                    
                    setLatestReportId(latestIncomeId);
                    setSelectedReportId(latestIncomeId);

                    // Fetch report data from /api/v1/reports/{id} to get distribution counts
                    await fetchDashboardReportData(latestIncomeId);
                    
                    // Also fetch detailed data for the latest report
                    await fetchReportData(latestIncomeId);
                }
            } else {
                console.error('Failed to fetch income reports or no reports available');
            }
        } catch (error) {
            console.error('Error fetching income reports:', error);
        } finally {
            setIsLoadingReports(false);
        }
    };

    // Fetch AFR distribution counts for dashboard badges
    const fetchDashboardReportData = async (incomeId) => {
        if (!incomeId) return;

        try {
            const response = await ProcessingReportService.getAfrCounts(incomeId);

            if (response && response.success && response.data) {
                setDistributionCounts({
                    title:    response.data.title    || 0,
                    dsp:      response.data.dsp      || 0,
                    country:  response.data.country  || 0,
                    typesale: response.data.typesale || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching AFR counts:', error);
        }
    };

    // Fetch report data with specific pagination parameters
    const fetchReportDataWithParams = async (reportId, params) => {
        if (!reportId) return;

        setIsLoading(true);
        try {
            console.log('Fetching report data with params:', params);

            // Determine which specialized endpoint to use based on the active tab
            let response;

            if (activeTab === 'title') {
                // Use the dedicated title distribution endpoint
                response = await ProcessingReportService.getTitleDistribution(
                    reportId,
                    params.title_page || 1,
                    params.title_per_page || 10
                );
            } else if (activeTab === 'dsp') {
                // Use the dedicated DSP distribution endpoint
                response = await ProcessingReportService.getDspDistribution(
                    reportId,
                    params.dsp_page || 1,
                    params.dsp_per_page || 10
                );
            } else if (activeTab === 'country') {
                // Use the dedicated country distribution endpoint
                response = await ProcessingReportService.getCountryDistribution(
                    reportId,
                    params.country_page || 1,
                    params.country_per_page || 10
                );
            } else if (activeTab === 'typesale') {
                // Use the dedicated typesale distribution endpoint
                response = await ProcessingReportService.getTypesaleDistribution(
                    reportId,
                    params.typesale_page || 1,
                    params.typesale_per_page || 10
                );
            } else {
                // Fallback to the general endpoint
                response = await ProcessingReportService.getReportById(reportId, params);
            }

            console.log('API Response:', response);

            if (response && response.success) {
                // Handle the response data
                const reportData = response.data;

                // Update distribution counts if available
                if (reportData.counts) {
                    // Map the counts to our state format
                    setDistributionCounts({
                        title: reportData.counts.title_distribution || 0,
                        dsp: reportData.counts.dsp_distribution || 0,
                        country: reportData.counts.country_distribution || 0,
                        typesale: reportData.counts.typesale_distribution || 0
                    });
                } else if (reportData.various_artists_distributions && reportData.various_artists_distributions.counts) {
                    const counts = reportData.various_artists_distributions.counts;
                    setDistributionCounts({
                        title: counts.title_distribution || 0,
                        dsp: counts.dsp_distribution || 0,
                        country: counts.country_distribution || 0,
                        typesale: counts.typesale_distribution || 0
                    });
                }

                // Set the distribution data based on the response format
                if (reportData.distributions) {
                    // Format 1: Data is in the distributions field (from dedicated endpoints)
                    setDistributionData(reportData.distributions || []);

                    // Update pagination info
                    if (reportData.pagination) {
                        setTotalItems(reportData.pagination.total || 0);
                        setTotalPages(reportData.pagination.total_pages || 1);
                        setCurrentPage(reportData.pagination.current_page || 1);
                        console.log('Pagination info from dedicated endpoint:', reportData.pagination);
                    }
                } else if (reportData.various_artists_distributions) {
                    // Format 2: Data is in the various_artists_distributions field (from general endpoint)
                    const distributions = {
                        title: reportData.various_artists_distributions.title_distribution || [],
                        dsp: reportData.various_artists_distributions.dsp_distribution || [],
                        country: reportData.various_artists_distributions.country_distribution || [],
                        typesale: reportData.various_artists_distributions.typesale_distribution || []
                    };

                    setDistributionData(distributions[activeTab] || []);

                    // Update pagination info
                    if (reportData.various_artists_distributions.pagination &&
                        reportData.various_artists_distributions.pagination[`${activeTab}_distribution`]) {
                        const paginationInfo = reportData.various_artists_distributions.pagination[`${activeTab}_distribution`];
                        setTotalItems(paginationInfo.total || 0);
                        setTotalPages(paginationInfo.total_pages || 1);
                        setCurrentPage(paginationInfo.current_page || 1);
                        console.log('Pagination info from general endpoint:', paginationInfo);
                    } else if (reportData.pagination && reportData.pagination[`${activeTab}_distribution`]) {
                        const paginationInfo = reportData.pagination[`${activeTab}_distribution`];
                        setTotalItems(paginationInfo.total || 0);
                        setTotalPages(paginationInfo.total_pages || 1);
                        setCurrentPage(paginationInfo.current_page || 1);
                        console.log('Pagination info from top level:', paginationInfo);
                    }
                } else {
                    setDistributionData([]);
                    console.warn('No distribution data found in response');
                }

                setIsFilterActive(true);
            } else {
                setDistributionData([]);
                console.error('Invalid API response:', response);
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            setDistributionData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch report data by ID
    const fetchReportData = async (reportId) => {
        if (!reportId) return;

        // Create pagination parameters based on current state
        const params = {};

        // Add pagination parameters for the active tab
        if (activeTab === 'title') {
            params.title_page = currentPage;
            params.title_per_page = itemsPerPage;
        } else if (activeTab === 'dsp') {
            params.dsp_page = currentPage;
            params.dsp_per_page = itemsPerPage;
        } else if (activeTab === 'country') {
            params.country_page = currentPage;
            params.country_per_page = itemsPerPage;
        } else if (activeTab === 'typesale') {
            params.typesale_page = currentPage;
            params.typesale_per_page = itemsPerPage;
        }

        // Use the helper function to fetch the data
        await fetchReportDataWithParams(reportId, params);
    };

    // Handle report selection
    const handleReportChange = async (e) => {
        const reportId = e.target.value;
        setSelectedReportId(reportId);

        if (reportId) {
            // Fetch distribution counts for the selected report
            await fetchDashboardReportData(reportId);
            fetchReportData(reportId);
        } else if (latestReportId) {
            // If no report is selected but we have a latest report, use that
            setSelectedReportId(latestReportId);
            await fetchDashboardReportData(latestReportId);
            fetchReportData(latestReportId);
        } else {
            // If no reports available, show empty state
            setIsFilterActive(false);
            setDistributionData([]);
            setDistributionCounts({
                title: 0,
                dsp: 0,
                country: 0,
                typesale: 0
            });
        }
    };

    // Fetch income reports on component mount
    useEffect(() => {
        fetchIncomeReports();
    }, []);

    // Fetch data when tab changes
    useEffect(() => {
        if (selectedReportId) {
            // Reset to page 1 when changing tabs
            const params = {};

            // Add pagination parameters for the active tab
            if (activeTab === 'title') {
                params.title_page = 1;
                params.title_per_page = itemsPerPage;
            } else if (activeTab === 'dsp') {
                params.dsp_page = 1;
                params.dsp_per_page = itemsPerPage;
            } else if (activeTab === 'country') {
                params.country_page = 1;
                params.country_per_page = itemsPerPage;
            } else if (activeTab === 'typesale') {
                params.typesale_page = 1;
                params.typesale_per_page = itemsPerPage;
            }

            // Reset current page in state
            setCurrentPage(1);

            // Fetch data with the new tab
            fetchReportDataWithParams(selectedReportId, params);
        } else if (latestReportId) {
            // If we have a latest report but no selected report, use the latest
            setSelectedReportId(latestReportId);

            // Create params for page 1
            const params = {};
            if (activeTab === 'title') {
                params.title_page = 1;
                params.title_per_page = itemsPerPage;
            } else if (activeTab === 'dsp') {
                params.dsp_page = 1;
                params.dsp_per_page = itemsPerPage;
            } else if (activeTab === 'country') {
                params.country_page = 1;
                params.country_per_page = itemsPerPage;
            } else if (activeTab === 'typesale') {
                params.typesale_page = 1;
                params.typesale_per_page = itemsPerPage;
            }

            fetchReportDataWithParams(latestReportId, params);
        } else {
            // If no reports available, show empty state
            setIsLoading(false);
            setDistributionData([]);
        }
    }, [activeTab]); // Only depend on activeTab, not on pagination state

    const handleViewReport = (reportId) => {
        navigate(appPath(`/income/report/${reportId}`));
    };

    // Function to handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const parseRowData = (item, index) => {
        const isRealApiData = isFilterActive && selectedReportId;
        let titleName = 'N/A';

        if (isRealApiData) {
            if (activeTab === 'title') {
                titleName = item.title_name || item.asset_title || 'N/A';
            } else if (activeTab === 'dsp') {
                const dspName = item.dsp_name;
                const assetIsrc = item.asset_isrc;
                titleName = dspName ? `${dspName}` : (assetIsrc ? `Asset: ${assetIsrc}` : 'N/A');
            } else if (activeTab === 'country') {
                const countryName = item.country_name;
                const assetIsrc = item.asset_isrc;
                titleName = countryName ? `${countryName}` : (assetIsrc ? `Asset: ${assetIsrc}` : 'N/A');
            } else if (activeTab === 'typesale') {
                const typesaleName = item.typesale_name;
                const assetIsrc = item.asset_isrc;
                titleName = typesaleName ? `${typesaleName}` : (assetIsrc ? `Asset: ${assetIsrc}` : 'N/A');
            }
        } else {
            titleName = item.title_name || 'N/A';
        }

        const isrc = isRealApiData ? (item.asset_isrc || item.isrc) : item.isrc;
        const upc = isRealApiData ? item.product_upc : item.product_upc;
        const revenue = isRealApiData ? (item.total_revenue || item.revenue) : item.revenue;
        const streams = isRealApiData ? item.streams : item.streams;
        const reportId = isRealApiData ? selectedReportId : (item.report_id || '1');

        let correctProduct;
        if (isRealApiData) {
            if (item.correct_product && typeof item.correct_product === 'object') {
                correctProduct = item.correct_product;
            } else if (item.correct_artist_name) {
                correctProduct = { artist_name: item.correct_artist_name };
            } else if (item.correct_artist) {
                correctProduct = typeof item.correct_artist === 'object'
                    ? item.correct_artist
                    : { artist_name: item.correct_artist };
            } else {
                correctProduct = { artist_name: 'Unknown Artist' };
            }
        } else {
            correctProduct = item.correct_product || { artist_name: 'Unknown Artist' };
        }

        const hasCorrectArtist = correctProduct && correctProduct.artist_name !== 'Unknown Artist';

        return {
            key: index,
            titleName,
            isrc,
            upc,
            revenue,
            streams,
            reportId,
            correctProduct,
            hasCorrectArtist,
        };
    };
    // Pagination handlers
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);

            // Immediately fetch data with the new page
            if (selectedReportId) {
                // Create pagination parameters based on current state
                const params = {};

                // Add pagination parameters for the active tab
                if (activeTab === 'title') {
                    params.title_page = page;
                    params.title_per_page = itemsPerPage;
                } else if (activeTab === 'dsp') {
                    params.dsp_page = page;
                    params.dsp_per_page = itemsPerPage;
                } else if (activeTab === 'country') {
                    params.country_page = page;
                    params.country_per_page = itemsPerPage;
                } else if (activeTab === 'typesale') {
                    params.typesale_page = page;
                    params.typesale_per_page = itemsPerPage;
                }

                // Fetch data with the new page
                fetchReportDataWithParams(selectedReportId, params);
            }
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page

        // Immediately fetch data with the new items per page
        if (selectedReportId) {
            // Create pagination parameters based on current state
            const params = {};

            // Add pagination parameters for the active tab
            if (activeTab === 'title') {
                params.title_page = 1; // Reset to page 1
                params.title_per_page = newItemsPerPage;
            } else if (activeTab === 'dsp') {
                params.dsp_page = 1; // Reset to page 1
                params.dsp_per_page = newItemsPerPage;
            } else if (activeTab === 'country') {
                params.country_page = 1; // Reset to page 1
                params.country_per_page = newItemsPerPage;
            } else if (activeTab === 'typesale') {
                params.typesale_page = 1; // Reset to page 1
                params.typesale_per_page = newItemsPerPage;
            }

            // Fetch data with the new items per page
            fetchReportDataWithParams(selectedReportId, params);
        }
    };

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];

        // Previous button
        items.push(
            <PaginationItem key="prev" disabled={currentPage === 1}>
                <PaginationLink previous onClick={(e) => {
                    e.preventDefault(); // Prevent default behavior
                    if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                    }
                }}>
                    <ChevronLeft size={14} />
                </PaginationLink>
            </PaginationItem>
        );

        // First page
        items.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink onClick={(e) => {
                    e.preventDefault(); // Prevent default behavior
                    handlePageChange(1);
                }}>
                    1
                </PaginationLink>
            </PaginationItem>
        );

        // Ellipsis if needed
        if (currentPage > 3) {
            items.push(
                <PaginationItem key="ellipsis1" disabled>
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Pages around current page
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (i > 1 && i < totalPages) {
                items.push(
                    <PaginationItem key={i} active={currentPage === i}>
                        <PaginationLink onClick={(e) => {
                            e.preventDefault(); // Prevent default behavior
                            handlePageChange(i);
                        }}>
                            {i}
                        </PaginationLink>
                    </PaginationItem>
                );
            }
        }

        // Ellipsis if needed
        if (currentPage < totalPages - 2) {
            items.push(
                <PaginationItem key="ellipsis2" disabled>
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Last page if not first page
        if (totalPages > 1) {
            items.push(
                <PaginationItem key={totalPages} active={currentPage === totalPages}>
                    <PaginationLink onClick={(e) => {
                        e.preventDefault(); // Prevent default behavior
                        handlePageChange(totalPages);
                    }}>
                        {totalPages}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        // Next button
        items.push(
            <PaginationItem key="next" disabled={currentPage === totalPages || totalPages === 0}>
                <PaginationLink next onClick={(e) => {
                    e.preventDefault(); // Prevent default behavior
                    if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                    }
                }}>
                    <ChevronRight size={14} />
                </PaginationLink>
            </PaginationItem>
        );

        return items;
    };

    return (
        <article className="uad-card">
            <div className="uad-card__header">
                <h2 className="uad-card__title">Unknown Artist Distributions</h2>
                <button
                    type="button"
                    className="uad-card__action"
                    onClick={() => navigate(appPath('/income/income-list'))}
                >
                    View all reports
                    <ExternalLink size={13} />
                </button>
            </div>

            <div className="uad-toolbar">
                <label htmlFor="reportFilter" className="uad-toolbar__label">
                    <Filter size={14} />
                    Report
                </label>
                <Input
                    type="select"
                    id="reportFilter"
                    className="uad-toolbar__select"
                    value={selectedReportId}
                    onChange={handleReportChange}
                >
                    <option value="">All distributions</option>
                    {isLoadingReports ? (
                        <option disabled>Loading reports...</option>
                    ) : (
                        incomeReports.map(report => (
                            <option key={report.id} value={report.id}>
                                {report.title} — {report.provider} ({report.period_start ? new Date(report.period_start).toLocaleDateString() : 'No date'})
                            </option>
                        ))
                    )}
                </Input>
                {selectedReportId && latestReportId && selectedReportId !== latestReportId && (
                    <button
                        type="button"
                        className="uad-toolbar__reset"
                        onClick={() => {
                            setSelectedReportId(latestReportId);
                            fetchReportData(latestReportId);
                        }}
                    >
                        Latest report
                    </button>
                )}
            </div>

            <div className="uad-tabs" role="tablist">
                {DISTRIBUTION_TABS.map(({ id, label }) => (
                    <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === id}
                        className={`uad-tab${activeTab === id ? ' uad-tab--active' : ''}`}
                        onClick={() => handleTabChange(id)}
                    >
                        <span className="uad-tab__label">{label}</span>
                        <span className="uad-tab__count">{distributionCounts[id]}</span>
                    </button>
                ))}
            </div>

            <div className="uad-card__body">
                {isLoading ? (
                    <div className="uad-loading">
                        <Spinner color="danger" size="sm" />
                        <span>Loading distribution data…</span>
                    </div>
                ) : distributionData.length > 0 ? (
                    <>
                        <div className="uad-table-wrap">
                            <table className="uad-table">
                                <thead>
                                    <tr>
                                        <th>{COLUMN_HEADERS[activeTab]}</th>
                                        <th>ISRC</th>
                                        <th>UPC</th>
                                        <th>Revenue</th>
                                        <th>Streams</th>
                                        <th>Artist</th>
                                        <th>Correct artist</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {distributionData.map((item, index) => {
                                        const row = parseRowData(item, index);
                                        return (
                                            <tr key={row.key}>
                                                <td className="uad-table__primary" title={row.titleName}>
                                                    {row.titleName || 'N/A'}
                                                </td>
                                                <td className="uad-table__mono">{row.isrc || '—'}</td>
                                                <td className="uad-table__mono">{row.upc || '—'}</td>
                                                <td>{row.revenue ? `${parseFloat(row.revenue).toFixed(2)} EUR` : '—'}</td>
                                                <td>{row.streams ?? '—'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="uad-link"
                                                        onClick={() => handleViewReport(row.reportId)}
                                                    >
                                                        Unknown Artist
                                                    </button>
                                                </td>
                                                <td>
                                                    <span className="uad-artist">
                                                        {row.hasCorrectArtist ? (
                                                            <>
                                                                <span className="uad-artist__dot" />
                                                                <span className="uad-artist__name">{row.correctProduct.artist_name}</span>
                                                            </>
                                                        ) : (
                                                            row.correctProduct.artist_name
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    {row.hasCorrectArtist ? (
                                                        <span className="uad-status uad-status--warn">
                                                            <AlertCircle size={12} />
                                                            Mismatch
                                                        </span>
                                                    ) : (
                                                        <span className="uad-status uad-status--error">
                                                            <AlertCircle size={12} />
                                                            Pending
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="uad-edit-btn"
                                                        title="Edit report"
                                                        onClick={() => handleViewReport(row.reportId)}
                                                    >
                                                        <Edit size={13} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="uad-footer">
                            <div className="uad-footer__meta">
                                <span>Rows per page</span>
                                <Input
                                    type="select"
                                    className="uad-footer__select"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </Input>
                                <span>
                                    {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                                </span>
                            </div>
                            <Pagination className="uad-pagination mb-0">
                                {renderPaginationItems()}
                            </Pagination>
                        </div>
                    </>
                ) : (
                    <div className="uad-empty">
                        No unknown artist distributions found in recent reports.
                    </div>
                )}
            </div>
        </article>
    );
};

export default UnknownArtistDistributions;
